import { clientStore } from 'ats/pages/clients/ClientStore';
import React from 'react';
import Select from 'react-select';
import MultiDropdownGrouped from 'ats/components/multi-dropdown-grouped/MultiDropdownGrouped';

export class ClientsDropdown extends React.Component {
	state = {
		loaded: false,
		inputValue: '',
		options: [],
		page: 1,
		hasMore: false,
		isLoading: false
	};

	MIN_SEARCH_CHARS = 3;
	PAGE_SIZE = 20;
	MAX_OPTIONS = 100;

	async componentDidMount() {
		this.setState({ loaded: true });
	}

	render() {
		if (this.state.loaded) {
			const { isMulti } = this.props;
			// Multi-select uses the grouped wrapper so currently selected
			// clients are pinned at the top and the count pill is rendered.
			const Component = isMulti ? MultiDropdownGrouped : Select;

			return (
				<Component
					menuPosition="fixed"
					classNamePrefix={
						this.props.classNamePrefix ||
						'react-select' +
							(this.props.styleVersion ? `-${this.props.styleVersion}` : '')
					}
					maxMenuHeight={300}
					className={
						'react-select ' +
						(isMulti ? 'ats-multi-dropdown ats-multi-dropdown-grouped ' : '') +
						(this.props.styleVersion ? this.props.styleVersion : '')
					}
					isMulti={isMulti}
					value={this.props.value}
					placeholder={this.props.placeholder || 'Clients'}
					options={this.state.options}
					isClearable={this.props.isClearable}
					isDisabled={this.props.disabled}
					isLoading={this.state.isLoading}
					components={{
						IndicatorSeparator: null,
						DropdownIndicator: () => (
							<img src="/ats/icons/chevron-down-icon.svg" alt="" />
						)
					}}
					styles={{
						container: (base, state) => ({
							...base,
							...(state.isDisabled && {
								opacity: 0.5,
								cursor: 'not-allowed'
							})
						}),
						control: base => ({
							...base,
							paddingRight: 12,
							...(this.props.customStyles?.control || {})
						})
					}}
					required={this.props.required}
					inputValue={this.state.inputValue}
					onInputChange={this.handleInputChange}
					getOptionValue={this.getOptionValue}
					getOptionLabel={this.getOptionLabel}
					noOptionsMessage={this.noOptionsMessage}
					onMenuScrollToBottom={this.handleMenuScrollToBottom}
					onChange={item => {
						this.setState({ inputValue: '' });
						if (this.props.onValueChange) {
							this.props.onValueChange(item);
						}
					}}
					{...this.props.AsyncProps}
				/>
			);
		} else {
			return null;
		}
	}

	handleInputChange = (newValue, { action }) => {
		if (action !== 'input-change') return newValue;

		const inputValue = (newValue || '').trimStart();
		this.setState(
			{
				inputValue,
				page: 1,
				hasMore: false,
				options: []
			},
			() => {
				this.debouncedSearch(inputValue);
			}
		);

		return inputValue;
	};

	debouncedSearchTimer = null;

	debouncedSearch = inputValue => {
		if (this.debouncedSearchTimer) {
			clearTimeout(this.debouncedSearchTimer);
		}

		this.debouncedSearchTimer = setTimeout(() => {
			this.searchFirstPage(inputValue);
		}, 250);
	};

	searchFirstPage = async inputValue => {
		const q = (inputValue || '').trim();
		if (q.length < this.MIN_SEARCH_CHARS) {
			this.setState({ options: [], hasMore: false, isLoading: false, page: 1 });
			return;
		}

		this.setState({ isLoading: true });
		try {
			const rows = await this.fetchClientsPage(q, 1);

			const selected = this.props.value
				? Array.isArray(this.props.value)
					? this.props.value
					: [this.props.value]
				: [];
			const byId = new Map();
			[...selected, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			const nextOptions = Array.from(byId.values()).slice(0, this.MAX_OPTIONS);

			this.setState({
				options: nextOptions,
				page: 1,
				hasMore:
					rows.length === this.PAGE_SIZE &&
					nextOptions.length < this.MAX_OPTIONS,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false, options: [], hasMore: false, page: 1 });
		}
	};

	handleMenuScrollToBottom = async () => {
		const q = (this.state.inputValue || '').trim();
		if (this.state.isLoading) return;
		if (!this.state.hasMore) return;
		if (q.length < this.MIN_SEARCH_CHARS) return;

		const nextPage = this.state.page + 1;
		this.setState({ isLoading: true });
		try {
			const rows = await this.fetchClientsPage(q, nextPage);

			const byId = new Map();
			[...this.state.options, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			const nextOptions = Array.from(byId.values()).slice(0, this.MAX_OPTIONS);

			this.setState({
				options: nextOptions,
				page: nextPage,
				hasMore:
					rows.length === this.PAGE_SIZE &&
					nextOptions.length < this.MAX_OPTIONS,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false });
		}
	};

	fetchClientsPage = async (text, page) => {
		const result = await clientStore.getClients({
			text,
			pagination: { page, limit: this.PAGE_SIZE }
		});
		return result.data.rows;
	};

	noOptionsMessage = value => {
		if (
			value.inputValue &&
			value.inputValue.trim().length >= this.MIN_SEARCH_CHARS
		) {
			return 'No matches found';
		} else {
			return `Type at least ${this.MIN_SEARCH_CHARS} characters to search clients`;
		}
	};

	getOptionValue = option => option.id;

	getOptionLabel = option => `${option.name}`;
}
